import { cloneElement, isValidElement, type MouseEvent, type ReactElement, useState } from "react";
import { clsx } from "clsx";

import { useDevice } from "@/shared/lib/hooks/useDevice";
import { ActionBtn } from "@/shared/ui/Btn";
import { Modal } from "@/shared/ui/Modal";

import type { DialogProps } from "../types";

import cls from "./Dialog.module.scss";

export const Dialog = (props: DialogProps) => {
  const { trigger, children, title, onClose, cancel, submit, isOpen: controlledIsOpen, isBusy } = props;

  const { isMobile } = useDevice();

  const isControlled = controlledIsOpen !== undefined;

  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);

  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;

  const handleOpen = () => {
    if (!isControlled) {
      setUncontrolledIsOpen(true);
    }
  };

  const handleClose = () => {
    if (!isControlled) {
      setUncontrolledIsOpen(false);
    }
    onClose?.();
  };

  const handleCancel = () => {
    if (cancel?.onClick) {
      cancel.onClick();
    }
    if (!cancel?.preventClosing) {
      handleClose();
    }
  };

  const handleSubmit = () => {
    if (submit?.onClick) {
      submit.onClick();
    }
  };

  const triggerElement =
    trigger && isValidElement(trigger)
      ? cloneElement(trigger as ReactElement<{ onClick?: (e: MouseEvent) => void }>, {
          onClick: (e: MouseEvent) => {
            const triggerProps = (trigger as ReactElement).props as { onClick?: (e: MouseEvent) => void };
            if (typeof triggerProps.onClick === "function") {
              triggerProps.onClick(e);
            }
            handleOpen();
          },
        })
      : trigger;

  return (
    <>
      {triggerElement}
      <Modal isOpen={isOpen} isBusy={isBusy} onClose={handleClose}>
        <div className={clsx(cls.Container, isMobile && cls.isMobile)}>
          <div className={cls.Content}>{title}</div>
          {children}
          <div className={cls.FooterWrapper}>
            <div className={cls.Footer}>
              {submit && (
                <ActionBtn
                  className={clsx(cls.Button, submit.className)}
                  onClick={handleSubmit}
                  disabled={submit.disabled}
                >
                  {submit.text}
                </ActionBtn>
              )}
              {cancel && (
                <ActionBtn className={clsx(cls.Button, cls.Cancel, cancel.className)} onClick={handleCancel}>
                  {cancel.text}
                </ActionBtn>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
